// Chaque appel qui fait partir un code par e-mail le note (`rememberCodeRequest`) :
// la page qu'ouvre le bouton de l'e-mail pourra alors le valider d'elle-même.
import { rememberCodeRequest } from '../utils/emailCodeLink';

// Mono-domaine (plan Asten §4-E) : le navigateur appelle l'API en same-origin
// sous /api. Un proxy retire le préfixe et route vers l'API : nginx en dev
// docker-compose, le webpack dev server (setupProxy.js) en `npm start`, Traefik
// en prod. Résultat : aucune requête cross-origin → plus besoin de CORS.
export const BASE_URL = process.env.REACT_APP_API_URL || '/api';

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse(res) {
  if (!res.ok) {
    if (res.status === 401) {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    const err = await res.json().catch(() => ({}));
    const detail = err.detail;
    if (Array.isArray(detail)) {
      // Pydantic préfixe les erreurs de validateur par « Value error, » : bruit
      // technique au-dessus d'un message déjà rédigé pour l'utilisatrice.
      const error = new Error(
        detail.map((d) => String(d.msg).replace(/^Value error, /, '')).join(' '),
      );
      error.status = res.status;
      throw error;
    }
    // Certaines erreurs portent un `detail` structuré {code, message} : le code
    // permet au front de réagir sans dépendre du libellé (cf. email_not_verified).
    const isStructured = detail && typeof detail === 'object';
    const error = new Error(
      (isStructured ? detail.message : detail) || `Erreur ${res.status}`,
    );
    error.status = res.status;
    if (isStructured) error.code = detail.code;
    throw error;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Auth ──────────────────────────────────────────────────

export async function apiLogin(email, password, deviceToken) {
  const params = { username: email, password };
  // Jeton d'appareil de confiance : s'il est présent et valide, le backend saute
  // l'OTP et renvoie directement les tokens (évite un email à chaque connexion).
  if (deviceToken) params.device_token = deviceToken;
  const body = new URLSearchParams(params);
  rememberCodeRequest();
  const res = await fetch(`${BASE_URL}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  return handleResponse(res);
}

// Renvoie le code de connexion sans repasser par le mot de passe. Répond 202 quoi
// qu'il arrive — y compris sur une adresse inconnue — pour ne pas devenir un
// oracle d'existence de comptes : il n'y a donc rien à interpréter en retour.
export async function apiResendLoginOtp(mail) {
  rememberCodeRequest();
  const res = await fetch(`${BASE_URL}/auth/resend-login-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail }),
  });
  return handleResponse(res);
}

export async function apiVerifyLoginOtp(mail, code, rememberDevice = true) {
  const res = await fetch(`${BASE_URL}/auth/verify-login-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail, code, remember_device: rememberDevice }),
  });
  return handleResponse(res);
}

export async function apiAdminLogin(email, password, adminToken, deviceToken) {
  const payload = { mail: email, password, admin_token: adminToken };
  // Même mécanisme que pour les sportives, avec une confiance plus courte (7 j) :
  // sans lui, chaque connexion admin déclenchait un envoi d'e-mail.
  if (deviceToken) payload.device_token = deviceToken;
  rememberCodeRequest();
  const res = await fetch(`${BASE_URL}/auth/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function apiCheckEmail(email) {
  const res = await fetch(`${BASE_URL}/users/check-email?email=${encodeURIComponent(email)}`);
  return handleResponse(res);
}

export async function apiRegisterSportive(data) {
  rememberCodeRequest();
  const res = await fetch(`${BASE_URL}/users/sportives/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiRegisterCoachRequest(data) {
  const res = await fetch(`${BASE_URL}/users/coaches/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiVerifyEmail(mail, code) {
  const res = await fetch(`${BASE_URL}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail, code }),
  });
  return handleResponse(res);
}

export async function apiResendVerification(mail) {
  rememberCodeRequest();
  const res = await fetch(`${BASE_URL}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail }),
  });
  return handleResponse(res);
}

// État du quota de renvoi (renvois restants + délai) sans consommer de renvoi.
export async function apiVerificationStatus(mail) {
  const res = await fetch(`${BASE_URL}/auth/verification-status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail }),
  });
  return handleResponse(res);
}

export async function apiForgotPassword(mail) {
  const res = await fetch(`${BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail }),
  });
  return handleResponse(res);
}

export async function apiResetPassword(mail, code, newPassword) {
  const res = await fetch(`${BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail, code, new_password: newPassword }),
  });
  return handleResponse(res);
}

export async function apiLogout(refreshToken) {
  const res = await fetch(`${BASE_URL}/auth/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  return handleResponse(res);
}

export async function apiRefreshToken(refreshToken) {
  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  return handleResponse(res);
}

// ── User ──────────────────────────────────────────────────

export async function apiGetMe(token) {
  const res = await fetch(`${BASE_URL}/users/me`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

/** Série de jours de saisie : `{ courant, record, derniere_activite }`. */
export async function apiGetStreak(token) {
  const res = await fetch(`${BASE_URL}/users/me/streak`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiUpdateMe(data, token) {
  const res = await fetch(`${BASE_URL}/users/me`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// Droit à la portabilité (Art. 20) : télécharge l'export JSON complet du compte.
// Passe par un blob plutôt qu'un lien : l'endpoint exige l'en-tête Authorization,
// qu'un simple <a href> n'enverrait pas.
export async function apiDownloadMyData(token) {
  const res = await fetch(`${BASE_URL}/users/me/export`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return handleResponse(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'cyclegala_export.json';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Retrait du consentement au traitement des données de santé : détruit relevés et
// profil de santé, referme les partages. Mot de passe exigé (geste irréversible).
export async function apiWithdrawHealthConsent(password, token) {
  const res = await fetch(`${BASE_URL}/users/me/health-consent/withdraw`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ password }),
  });
  return handleResponse(res);
}

export async function apiGrantHealthConsent(token) {
  const res = await fetch(`${BASE_URL}/users/me/health-consent`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ consent_accepted: true }),
  });
  return handleResponse(res);
}

// ── Admin ─────────────────────────────────────────────────

export async function apiGetAdminDashboard(token) {
  const res = await fetch(`${BASE_URL}/admin/dashboard`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiSearchUnverifiedAccount(email, token) {
  const res = await fetch(`${BASE_URL}/admin/unverified-accounts/search?email=${encodeURIComponent(email)}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiValidateAccount(id, token) {
  const res = await fetch(`${BASE_URL}/admin/unverified-accounts/${id}/validate`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiResendVerificationAdmin(id, token) {
  const res = await fetch(`${BASE_URL}/admin/unverified-accounts/${id}/resend-code`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiGetCoachRequests(token) {
  const res = await fetch(`${BASE_URL}/admin/coach-requests`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiApproveCoachRequest(id, token) {
  const res = await fetch(`${BASE_URL}/admin/coach-requests/${id}/approve`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiRejectCoachRequest(id, token) {
  const res = await fetch(`${BASE_URL}/admin/coach-requests/${id}/reject`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Clôt définitivement une demande refusée : efface les données identifiantes et
// libère l'adresse email, qui redevient utilisable pour une nouvelle demande.
export async function apiAnonymizeCoachRequest(id, token) {
  const res = await fetch(`${BASE_URL}/admin/coach-requests/${id}/anonymize`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Équipes du coach ────────────────────────────────────────────────────────
// Un simple regroupement des sportives suivies, pour filtrer le tableau de bord.
// N'ouvre aucun droit : l'accès aux données reste porté par le suivi et ses flags
// de partage.

export async function apiGetTeams(token) {
  const res = await fetch(`${BASE_URL}/equipes`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function apiCreateTeam(payload, token) {
  const res = await fetch(`${BASE_URL}/equipes`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function apiUpdateTeam(id, payload, token) {
  const res = await fetch(`${BASE_URL}/equipes/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function apiDeleteTeam(id, token) {
  const res = await fetch(`${BASE_URL}/equipes/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// PUT et non POST : rattacher deux fois la même sportive doit être sans effet.
export async function apiAddTeamMember(teamId, sportiveId, token) {
  const res = await fetch(`${BASE_URL}/equipes/${teamId}/membres/${sportiveId}`, {
    method: 'PUT',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiRemoveTeamMember(teamId, sportiveId, token) {
  const res = await fetch(`${BASE_URL}/equipes/${teamId}/membres/${sportiveId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Effacement d'un compte utilisateur (RGPD Art. 17) ───────────────────────
// Recours pour les demandes reçues hors application : DELETE /users/me exige une
// session, dont ne disposent ni la personne qui a perdu l'accès à son compte, ni
// le coach déjà validé — sorti du flux des demandes de coach.

export async function apiSearchAccount(email, token) {
  const res = await fetch(`${BASE_URL}/admin/accounts/search?email=${encodeURIComponent(email)}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Le mot de passe de l'admin est redemandé côté serveur : l'action est
// irréversible et porte sur les données d'un tiers.
export async function apiAnonymizeAccount(id, password, token) {
  const res = await fetch(`${BASE_URL}/admin/accounts/${id}/anonymize`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  return handleResponse(res);
}

// Anonymisation + destruction des relevés de santé. Contrairement à la suppression
// dure, reste possible quel que soit l'historique d'audit : la ligne `client`
// subsiste en coquille, seule référence dont le journal a besoin.
export async function apiPurgeAccount(id, password, token) {
  const res = await fetch(`${BASE_URL}/admin/accounts/${id}/purge`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  return handleResponse(res);
}

// Suppression réelle de la ligne. Le serveur la refuse (409) dès que le compte a
// un historique d'audit — d'où `can_hard_delete` renvoyé par la recherche.
export async function apiDeleteAccount(id, password, token) {
  const res = await fetch(`${BASE_URL}/admin/accounts/${id}`, {
    method: 'DELETE',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  return handleResponse(res);
}

// ── Support (fil de discussion utilisateur ↔ administration) ────────────────
// Aucun e-mail n'est déclenché par ces routes : le fil se consulte dans l'app.
// `apiListTickets` sert les deux côtés — un utilisateur reçoit ses tickets, un
// admin les reçoit tous, le filtrage se fait côté serveur d'après le rôle.

export async function apiListTickets(token, statut) {
  const query = statut ? `?statut=${encodeURIComponent(statut)}` : '';
  const res = await fetch(`${BASE_URL}/support/tickets${query}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiGetTicket(id, token) {
  const res = await fetch(`${BASE_URL}/support/tickets/${id}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateTicket(payload, token) {
  const res = await fetch(`${BASE_URL}/support/tickets`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function apiReplyToTicket(id, message, token) {
  const res = await fetch(`${BASE_URL}/support/tickets/${id}/messages`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  return handleResponse(res);
}

export async function apiUpdateTicketStatus(id, statut, token) {
  const res = await fetch(`${BASE_URL}/support/tickets/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ statut }),
  });
  return handleResponse(res);
}

// ── Autorisation parentale des sportives mineures ───────────────────────────
// Le formulaire signé est recueilli par le club hors application : ces routes ne
// font qu'enregistrer la trace que l'autorisation a été vue, et par quel admin.

export async function apiGetGuardianPending(token) {
  const res = await fetch(`${BASE_URL}/admin/guardian-consent/pending`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiRecordGuardianConsent(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/admin/guardian-consent/${sportiveId}`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Accès au module questionnaires (étude quantitative) ─────────────────────
// Ces routes ne donnent aucun accès aux données de santé de l'app : elles gèrent
// des accès au service questionnaires, qui vit dans une base distincte.

export async function apiGetSurveyAccesses(token) {
  const res = await fetch(`${BASE_URL}/admin/survey-access`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Renvoie l'accès créé, sans aucun secret : le code d'activation part directement
// par e-mail au destinataire, il ne transite jamais par cette interface.
export async function apiIssueSurveyAccess(payload, token) {
  const res = await fetch(`${BASE_URL}/admin/survey-access`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Coupe l'accès en gardant la trace de qui l'a détenu et de ses usages.
export async function apiRevokeSurveyAccess(id, token) {
  const res = await fetch(`${BASE_URL}/admin/survey-access/${id}/revoke`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Efface la ligne, nom et adresse du chercheur compris. Répond à une demande
// d'effacement : rien d'autre ne purge ces données côté questionnaires.
export async function apiDeleteSurveyAccess(id, token) {
  const res = await fetch(`${BASE_URL}/admin/survey-access/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Accès à la rédaction du blog ────────────────────────────────────────────
// Écrire sur le blog ne donne accès ni aux données de santé ni au dashboard.
// Deux populations : les comptes de rédaction dédiés (`type_client = editor`) et
// les comptes ordinaires porteurs de `can_edit_blog`. La liste renvoie les deux.

export async function apiGetEditors(token) {
  const res = await fetch(`${BASE_URL}/admin/editors`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Aucun mot de passe n'est choisi ici : le compte créé reçoit par e-mail un code
// avec lequel l'éditeur définit lui-même le sien.
export async function apiCreateEditor(payload, token) {
  const res = await fetch(`${BASE_URL}/admin/editors`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Ouvre le blog à un compte qui existe déjà : son rôle et ses données ne bougent
// pas, il gagne seulement la capacité d'écrire.
export async function apiGrantBlogAccess(mail, token) {
  const res = await fetch(`${BASE_URL}/admin/blog-access`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail }),
  });
  return handleResponse(res);
}

// Retire la seule capacité. Le serveur refuse (409) sur un compte de rédaction
// dédié, dont le type rouvrirait l'accès : celui-là s'efface, il ne se révoque pas.
export async function apiRevokeBlogAccess(id, token) {
  const res = await fetch(`${BASE_URL}/admin/blog-access/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Membres du dashboard de gouvernance ─────────────────────────────────────
// L'admin crée le membre, qui choisit lui-même son mot de passe avec le code reçu
// par e-mail. `password` est celui de l'admin, revérifié par le serveur (403 si faux).

export async function apiGetGovMembers(token) {
  const res = await fetch(`${BASE_URL}/admin/gov-members`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateGovMember(payload, token) {
  const res = await fetch(`${BASE_URL}/admin/gov-members`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// ── Identifiants de l'administrateur ────────────────────────────────────────
// Seule voie applicative de changement : les comptes admin sont exclus du flux
// « mot de passe oublié ». La réponse ne porte un `admin_token` que si sa
// rotation a été demandée, et il n'est plus jamais réaffichable ensuite.
export async function apiChangeAdminPassword(payload, token) {
  const res = await fetch(`${BASE_URL}/admin/me/password`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function apiGetAuditLogs(token, filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });
  const res = await fetch(`${BASE_URL}/admin/audit-logs?${params.toString()}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiUploadAvatar(file, token) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE_URL}/users/me/avatar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  return handleResponse(res);
}

// ── Blog ──────────────────────────────────────────────────

export async function apiGetArticles(token) {
  const res = await fetch(`${BASE_URL}/articles`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Vue de l'espace rédaction : tous les articles, brouillons et programmés compris.
// `apiGetArticles` ne sert plus que ce qui est effectivement en ligne, y compris pour
// une rédactrice — d'où cette route distincte plutôt qu'un paramètre sur la première.
export async function apiGetArticlesRedaction(token) {
  const res = await fetch(`${BASE_URL}/articles/redaction`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiGetArticle(id, token) {
  const res = await fetch(`${BASE_URL}/articles/${id}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateArticle(data, token) {
  const res = await fetch(`${BASE_URL}/articles`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiUpdateArticle(id, data, token) {
  const res = await fetch(`${BASE_URL}/articles/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiDeleteArticle(id, token) {
  const res = await fetch(`${BASE_URL}/articles/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Cycle ─────────────────────────────────────────────────

export async function apiGetCycles(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/cycles/sportive/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateCycle(data, token) {
  const res = await fetch(`${BASE_URL}/cycles/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiGetCyclePhases(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/cycles/sportive/${sportiveId}/phases`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// `cycleOffset` remonte d'un cycle à la fois : 0 = cycle en cours, 1 = le précédent.
// La courbe se lit en jours de cycle, deux cycles superposés y confondraient leurs J.
export async function apiGetCycleTemperatures(sportiveId, token, cycleOffset = 0) {
  const query = cycleOffset ? `?cycle_offset=${cycleOffset}` : '';
  const res = await fetch(`${BASE_URL}/cycles/sportive/${sportiveId}/temperatures${query}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiUpdateCycle(cycleId, data, token) {
  const res = await fetch(`${BASE_URL}/cycles/${cycleId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// ── Mental ────────────────────────────────────────────────

export async function apiGetMentals(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/mentals/sportive/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateMental(data, token) {
  const res = await fetch(`${BASE_URL}/mentals/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiUpdateMental(mentalId, data, token) {
  const res = await fetch(`${BASE_URL}/mentals/${mentalId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// ── Sport ─────────────────────────────────────────────────

export async function apiGetSports(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/sports/sportive/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateSport(data, token) {
  const res = await fetch(`${BASE_URL}/sports/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiUpdateSport(sportId, data, token) {
  const res = await fetch(`${BASE_URL}/sports/${sportId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// ── Suivi ─────────────────────────────────────────────────

export async function apiGetSuivisForCoach(coachId, token) {
  const res = await fetch(`${BASE_URL}/suivis/coach/${coachId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiGetSuivisForSportive(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/suivis/sportive/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiUpdateSuivi(suiviId, data, token) {
  const res = await fetch(`${BASE_URL}/suivis/${suiviId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiGetSportive(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/users/sportives/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiSearchSportive(email, token) {
  const res = await fetch(`${BASE_URL}/users/sportives/search?email=${encodeURIComponent(email)}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiCreateSuivi(data, token) {
  const res = await fetch(`${BASE_URL}/suivis/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiGetPendingInvitations(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/suivis/sportive/${sportiveId}/invitations`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function apiRespondToInvitation(suiviId, accept, token) {
  const res = await fetch(`${BASE_URL}/suivis/${suiviId}/respond`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ accept }),
  });
  return handleResponse(res);
}

// ── Dashboard ──────────────────────────────────────────────

export async function apiGetCycleStats(sportiveId, token, symptomsDays = 30) {
  const res = await fetch(
    `${BASE_URL}/cycles/sportive/${sportiveId}/stats?symptoms_days=${symptomsDays}`,
    { headers: authHeaders(token) }
  );
  return handleResponse(res);
}

export async function apiGetDashboardSummary(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/dashboard/sportive/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ── Insights : profil sport & mental par phase du cycle ──────────────────────
// Un seul appel pour les deux domaines : ils doivent être servis sur la même fenêtre
// et la même granularité que le cycle.
export async function apiGetPhaseInsights(sportiveId, token) {
  const res = await fetch(`${BASE_URL}/insights/sportive/${sportiveId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}
